import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  CopyButton,
  CopyableValue,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Td,
} from '../components/ui/primitives';
import { dateTime, gradeColour, pct } from '../lib/format';
import { DmarcPanel, type DmarcAnalysis } from '../components/deliverability/DmarcPanel';
import {
  DmarcSourcesPanel,
  type DmarcSourcesData,
} from '../components/deliverability/DmarcSourcesPanel';

interface DnsCheck {
  name: string;
  expected: string | null;
  found: string | null;
  valid: boolean;
  note: string | null;
}

interface DomainDetail {
  domain: {
    id: string;
    domain: string;
    status: string;
    exclusivity: string;
    trackingDomain: string | null;
    dailyCap: number;
    hourlyCap: number;
  };
  reputation: {
    score: number;
    grade: string;
    components: Array<{ key: string; label: string; weight: number; score: number; detail: string }>;
    capturedAt: string;
  } | null;
  history: Array<{ capturedAt: string; score: number; grade: string }>;
  dns: {
    score: number;
    issues: string[];
    checkedAt: string;
    spf: DnsCheck;
    dkim: DnsCheck;
    dmarc: DnsCheck;
    mx: DnsCheck;
    trackingCname: DnsCheck;
    bimi: DnsCheck;
  } | null;
  listings: Array<{
    provider: string;
    zone: string;
    listed: boolean;
    critical: boolean;
    reason: string | null;
    delistUrl: string | null;
  }>;
  clients: Array<{ id: string; name: string }>;
  identities: Array<{
    id: string;
    email: string;
    displayName: string;
    status: string;
    dailyCap: number;
    hourlyCap: number;
    minGapSeconds: number;
  }>;
  warmup: { profile: string; day: number; totalDays: number; pct: number; complete: boolean } | null;
  seedTests: Array<{ id: string; name: string; status: string; inbox: number; junk: number; missing: number; total: number }>;
  dnsSetup: {
    provider: string;
    providerName: string;
    valuesConfirmed: boolean;
    guidance: string;
    outstanding: number;
    needsDecision: number;
    nonDestructive: boolean;
    records: Array<{
      key: string;
      label: string;
      type: string;
      host: string;
      hostRelative: string;
      value: string | null;
      obtainFrom: string | null;
      required: boolean;
      note: string;
      status: 'ok' | 'missing' | 'attention' | 'unknown';
      found: string | null;
      action: 'keep' | 'merge' | 'add' | 'replace' | 'obtain' | 'manual';
      current: string | null;
      preserved: string[];
      mergeNotes: string[];
    }>;
  };
  dmarc: DmarcAnalysis | null;
}

/**
 * What each suggested value does to what is already published. The wording
 * matters more than it looks: "replace" is the only one that destroys
 * anything, so it is the only one coloured as a warning.
 */
interface DomainUsage {
  identities: number;
  messages: number;
  trackingEvents: number;
  campaigns: number;
  clients: number;
  seedTests: number;
  identityEmails: string[];
}

const ACTION_LABEL: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'info' | 'neutral'; hint: string }> = {
  keep: { label: 'keep', tone: 'good', hint: 'Already correct - publish nothing.' },
  merge: { label: 'merge', tone: 'info', hint: 'Extends the record already published; nothing existing is removed.' },
  add: { label: 'add', tone: 'info', hint: 'Nothing is published at this host yet.' },
  replace: { label: 'repoint', tone: 'warn', hint: 'Something else is published here and would be replaced.' },
  obtain: { label: 'get value', tone: 'neutral', hint: 'Only the provider can supply this.' },
  manual: { label: 'your call', tone: 'warn', hint: 'A safe value cannot be derived - see the note.' },
};

export function DomainDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mailbox, setMailbox] = useState({ localPart: '', displayName: '' });
  const [seed, setSeed] = useState({ identityId: '', name: 'Placement check' });
  const [message, setMessage] = useState<string | null>(null);

  const [dmarcScanning, setDmarcScanning] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sourceDays, setSourceDays] = useState(30);

  const { data: dmarcSources } = useQuery({
    queryKey: ['dmarc-sources', id, sourceDays],
    queryFn: () =>
      api.get<DmarcSourcesData>('/api/domains/' + id + '/dmarc/sources?days=' + sourceDays),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['domain', id],
    queryFn: () => api.get<DomainDetail>('/api/domains/' + id),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['domain', id] });

  const dnsCheck = useMutation({
    mutationFn: () => api.post('/api/domains/' + id + '/dns-check'),
    onSuccess: refresh,
  });
  const repScan = useMutation({
    mutationFn: () => api.post('/api/domains/' + id + '/reputation-scan'),
    onSuccess: refresh,
  });
  const addMailbox = useMutation({
    mutationFn: () => api.post('/api/identities', { domainId: id, ...mailbox }),
    onSuccess: () => {
      setMailbox({ localPart: '', displayName: '' });
      refresh();
    },
  });
  /** Pausing stops new sends and holds anything already queued. */
  const setIdentityStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      api.patch('/api/identities/' + v.id, { status: v.status }),
    onSuccess: refresh,
    onError: (err: Error) => setMessage(err.message),
  });

  const removeIdentity = useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>('/api/identities/' + id),
    onSuccess: (r) => {
      setMessage(r.message);
      refresh();
    },
    // A 409 explains that send history pins the mailbox.
    onError: (err: Error) => setMessage(err.message),
  });

  const removeDomain = useMutation({
    mutationFn: () => api.delete<{ message: string }>('/api/domains/' + id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      void queryClient.invalidateQueries({ queryKey: ['dmarc-overview'] });
      navigate('/domains');
    },
    onError: (err: Error) => setMessage(err.message),
  });

  /**
   * Counts come from the server immediately before the prompt rather than
   * from the page data, which may be minutes stale. The whole point of the
   * prompt is the numbers in it, so they have to be current.
   */
  const confirmAndRemove = async () => {
    // Declared above the loading guard, so it has to narrow `data` itself.
    if (!data) return;
    let usage: DomainUsage;
    try {
      usage = await api.get<DomainUsage>('/api/domains/' + id + '/usage');
    } catch (err) {
      setMessage((err as Error).message);
      return;
    }

    const losses = [
      usage.identities ? usage.identities + ' mailbox(es)' : null,
      usage.messages ? usage.messages + ' sent message(s)' : null,
      usage.trackingEvents ? usage.trackingEvents + ' tracking event(s) - opens and clicks' : null,
      usage.seedTests ? usage.seedTests + ' seed test(s)' : null,
      usage.clients ? 'access for ' + usage.clients + ' client(s)' : null,
      'its DNS and DMARC history',
    ].filter(Boolean);

    const warning = usage.messages
      ? '\n\nThis domain has send history. Deleting it removes those messages and the opens and clicks recorded against them, so engagement and placement figures covering this domain will drop. What each provider told us is kept in the raw webhook archive.'
      : '';

    if (
      window.confirm(
        'Permanently delete ' +
          data.domain.domain +
          '?\n\nThis also deletes:\n  - ' +
          losses.join('\n  - ') +
          warning +
          '\n\nThis cannot be undone.'
      )
    ) {
      removeDomain.mutate();
    }
  };

  const runSeed = useMutation({
    mutationFn: () => api.post('/api/seed-tests', { domainId: id, ...seed }),
    onSuccess: () => {
      setMessage('Seed test started. Results appear as the probe arrives in each mailbox.');
      refresh();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  if (isLoading || !data) return <Loading />;

  const dnsRows = data.dns
    ? [data.dns.spf, data.dns.dkim, data.dns.dmarc, data.dns.mx, data.dns.trackingCname, data.dns.bimi]
    : [];

  return (
    <>
      <PageHeader
        title={data.domain.domain}
        description={
          data.domain.exclusivity === 'exclusive'
            ? 'Exclusive domain. Caps can be pushed harder because only one client affects its reputation.'
            : 'Shared domain. Several clients send from it, so one bad list damages everyone - caps and the circuit breaker are deliberately tighter.'
        }
        actions={
          <>
            <Button loading={dnsCheck.isPending} onClick={() => dnsCheck.mutate()}>
              Re-check DNS
            </Button>
            <Button variant="primary" loading={repScan.isPending} onClick={() => repScan.mutate()}>
              Run reputation scan
            </Button>
            <Button
              variant="danger"
              loading={removeDomain.isPending}
              onClick={() => void confirmAndRemove()}
            >
              Delete domain
            </Button>
          </>
        }
      />

      {message && (
        <div className="mb-5">
          <Callout tone="info">{message}</Callout>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid gap-5 lg:grid-cols-3">
          <Card title="Reputation score" className="lg:col-span-2">
            {!data.reputation ? (
              <EmptyState title="Not scanned yet" body="Run a reputation scan to build the first snapshot." />
            ) : (
              <>
                <div className="mb-5 flex items-center gap-4">
                  <span
                    className={
                      'rounded-xl border px-4 py-2 text-2xl font-semibold tabular-nums ' +
                      gradeColour(data.reputation.grade)
                    }
                  >
                    {data.reputation.score.toFixed(1)}
                  </span>
                  <div className="text-sm text-ink-500">
                    Grade {data.reputation.grade} - measured {dateTime(data.reputation.capturedAt)}
                  </div>
                </div>
                <Table head={['Component', 'Weight', 'Score', 'Detail']}>
                  {data.reputation.components.map((c) => (
                    <tr key={c.key}>
                      <Td className="font-medium text-ink-900">{c.label}</Td>
                      <Td className="tabular-nums">{c.weight}%</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 rounded-full bg-ink-200">
                            <div
                              className={
                                'h-1.5 rounded-full ' +
                                (c.score >= 80 ? 'bg-emerald-500' : c.score >= 55 ? 'bg-amber-500' : 'bg-red-500')
                              }
                              style={{ width: Math.max(2, c.score) + '%' }}
                            />
                          </div>
                          <span className="tabular-nums text-xs">{c.score.toFixed(0)}</span>
                        </div>
                      </Td>
                      <Td className="max-w-md text-xs text-ink-500">{c.detail}</Td>
                    </tr>
                  ))}
                </Table>
              </>
            )}
          </Card>

          <Card title="Warmup">
            {!data.warmup ? (
              <EmptyState title="No warmup plan" />
            ) : (
              <div>
                <div className="text-sm text-ink-700">
                  Day {data.warmup.day} of {data.warmup.totalDays} ({data.warmup.profile})
                </div>
                <div className="mt-3 h-2 w-full rounded-full bg-ink-200">
                  <div className="h-2 rounded-full bg-ink-900" style={{ width: data.warmup.pct + '%' }} />
                </div>
                <p className="mt-3 text-xs leading-relaxed text-ink-500">
                  Volume is capped at the curve for today regardless of the configured daily cap. The
                  ceiling lifts automatically as the ramp progresses.
                </p>
                <div className="mt-4 space-y-1 text-xs text-ink-500">
                  <div>Daily cap: {data.domain.dailyCap.toLocaleString()}</div>
                  <div>Hourly cap: {data.domain.hourlyCap.toLocaleString()}</div>
                </div>
              </div>
            )}
          </Card>
        </div>

        <Card
          title="Authentication"
          subtitle={
            data.dns
              ? 'Score ' + data.dns.score + '/100, checked ' + dateTime(data.dns.checkedAt)
              : 'Not checked yet'
          }
        >
          {dnsRows.length === 0 ? (
            <EmptyState title="No DNS check recorded" />
          ) : (
            <Table head={['Record', 'Status', 'Found', 'Note']}>
              {dnsRows.map((row) => (
                <tr key={row.name}>
                  <Td className="font-medium text-ink-900">{row.name}</Td>
                  <Td>
                    <Badge tone={row.valid ? 'good' : 'warn'}>{row.valid ? 'ok' : 'attention'}</Badge>
                  </Td>
                  <Td className="max-w-md break-all font-mono text-[11px] text-ink-600">
                    {row.found ?? '--'}
                  </Td>
                  <Td className="max-w-md text-xs text-ink-500">{row.note}</Td>
                </tr>
              ))}
            </Table>
          )}

        </Card>

        <DmarcPanel
          analysis={data.dmarc}
          rescanning={dmarcScanning}
          onRescan={async () => {
            setDmarcScanning(true);
            try {
              // persist:true so a manual scan also reconciles the alert list,
              // which is what someone pressing this button is usually after.
              await api.post('/api/domains/' + id + '/dmarc-scan', { persist: true });
              await queryClient.invalidateQueries({ queryKey: ['domain', id] });
              setMessage('DMARC re-scanned.');
            } finally {
              setDmarcScanning(false);
            }
          }}
        />

        <DmarcSourcesPanel
          data={dmarcSources ?? null}
          days={sourceDays}
          onDaysChange={setSourceDays}
          uploading={uploading}
          onUpload={async (files) => {
            setUploading(true);
            try {
              const form = new FormData();
              for (const file of Array.from(files)) form.append('files', file);
              const result = await api.post<{
                attachments: number;
                stored: number;
                duplicates: number;
                messages: number;
                domains: string[];
                unmanagedDomains: string[];
                failed: Array<{ filename: string; reason: string }>;
              }>('/api/dmarc/upload', form);
              await queryClient.invalidateQueries({ queryKey: ['dmarc-sources', id] });
              await queryClient.invalidateQueries({ queryKey: ['dmarc-report-coverage'] });
              setMessage(
                'Loaded ' +
                  result.stored +
                  ' report(s) covering ' +
                  result.messages.toLocaleString() +
                  ' message(s)' +
                  (result.duplicates ? ', ' + result.duplicates + ' already stored' : '') +
                  (result.unmanagedDomains.length
                    ? '. Note: ' +
                      result.unmanagedDomains.join(', ') +
                      ' is not a sending domain here, so its reports are stored but shown under Deliverability'
                    : '') +
                  (result.failed.length
                    ? '. ' +
                      result.failed.length +
                      ' could not be read: ' +
                      result.failed.map((f) => f.filename + ' (' + f.reason + ')').join('; ')
                    : '.')
              );
            } catch (err) {
              setMessage((err as Error).message);
            } finally {
              setUploading(false);
            }
          }}
          ingesting={ingesting}
          onIngest={async () => {
            setIngesting(true);
            try {
              const result = await api.post<{
                attachments: number;
                stored: number;
                duplicates: number;
                failed: Array<{ filename: string; reason: string }>;
              }>('/api/dmarc/ingest', {});
              await queryClient.invalidateQueries({ queryKey: ['dmarc-sources', id] });
              setMessage(
                'Collected ' +
                  result.stored +
                  ' new report(s) from ' +
                  result.attachments +
                  ' attachment(s)' +
                  (result.duplicates ? ', ' + result.duplicates + ' already stored' : '') +
                  (result.failed.length ? ', ' + result.failed.length + ' could not be read' : '') +
                  '.'
              );
            } catch (err) {
              setMessage((err as Error).message);
            } finally {
              setIngesting(false);
            }
          }}
        />

        <Card
          title={'Records to publish for ' + data.dnsSetup.providerName}
          subtitle={
            data.dnsSetup.outstanding === 0
              ? 'Everything required is in place.'
              : data.dnsSetup.outstanding +
                ' required record(s) still outstanding. Publish these at your DNS host, then press Re-check DNS.'
          }
        >
          {!data.dnsSetup.valuesConfirmed && (
            <div className="mb-4">
              <Callout tone="warn" title="Some values must come from the provider">
                {data.dnsSetup.guidance}
              </Callout>
            </div>
          )}

          {data.dnsSetup.needsDecision > 0 && (
            <div className="mb-4">
              <Callout tone="warn" title="Some records need a decision from you">
                {data.dnsSetup.needsDecision} record(s) already have something published that
                cannot be merged automatically. No value is suggested for those - the note on each
                says what has to be resolved first.
              </Callout>
            </div>
          )}

          <Table head={['', 'Type', 'Host', 'Value to publish', 'Currently']}>
            {data.dnsSetup.records.map((r) => (
              <tr key={r.key}>
                <Td>
                  <div className="flex items-center gap-2">
                    <Badge
                      tone={
                        r.status === 'ok'
                          ? 'good'
                          : r.status === 'attention'
                            ? 'warn'
                            : r.status === 'missing'
                              ? r.required
                                ? 'bad'
                                : 'neutral'
                              : 'neutral'
                      }
                    >
                      {r.status === 'ok' ? 'ok' : r.status === 'attention' ? 'fix' : 'todo'}
                    </Badge>
                    <span className="font-medium text-ink-900">{r.label}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <Badge tone={ACTION_LABEL[r.action]?.tone ?? 'neutral'}>
                      {ACTION_LABEL[r.action]?.label ?? r.action}
                    </Badge>
                    {!r.required && <span className="text-[11px] text-ink-400">optional</span>}
                  </div>
                </Td>
                <Td className="text-xs">{r.type}</Td>
                <Td className="max-w-[17rem]">
                  {/* Most registrars want the name relative to the zone, so
                      that is offered first; GoDaddy in particular appends the
                      domain to whatever you type. */}
                  <CopyableValue value={r.hostRelative} />
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 break-all font-mono text-[10px] text-ink-400">
                      {r.host}
                    </span>
                    <CopyButton value={r.host} label="Copy full" title={'Copy ' + r.host} />
                  </div>
                </Td>
                <Td className="max-w-md">
                  {r.value ? (
                    <CopyableValue value={r.value} />
                  ) : (
                    <span className="text-xs text-amber-700">
                      Get from: {r.obtainFrom ?? 'your provider'}
                    </span>
                  )}
                  <p className="mt-1 text-[11px] leading-relaxed text-ink-500">{r.note}</p>
                  {r.action === 'merge' && r.preserved.length > 0 && (
                    <p className="mt-1 text-[11px] leading-relaxed text-emerald-700">
                      Keeps: {r.preserved.join(' ')}
                    </p>
                  )}
                </Td>
                <Td className="max-w-[14rem] break-all font-mono text-[11px] text-ink-500">
                  {r.found ?? <span className="font-sans text-ink-400">not found</span>}
                </Td>
              </tr>
            ))}
          </Table>

          {data.dnsSetup.valuesConfirmed && data.dnsSetup.guidance && (
            <p className="mt-4 text-xs leading-relaxed text-ink-500">{data.dnsSetup.guidance}</p>
          )}
        </Card>

        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="Blocklist status" subtitle="Checked directly over DNS - no third-party service required.">
            {data.listings.length === 0 ? (
              <EmptyState title="No blocklist data yet" body="Run a reputation scan to query every monitored zone." />
            ) : (
              <Table head={['List', 'Status', 'Reason']}>
                {data.listings.map((l) => (
                  <tr key={l.provider + l.zone}>
                    <Td>
                      <div className="font-medium text-ink-900">{l.provider}</div>
                      <code className="text-[11px] text-ink-400">{l.zone}</code>
                    </Td>
                    <Td>
                      {l.listed ? (
                        <Badge tone={l.critical ? 'bad' : 'warn'}>listed</Badge>
                      ) : (
                        <Badge tone="good">clear</Badge>
                      )}
                    </Td>
                    <Td className="text-xs text-ink-500">
                      {l.reason ?? '--'}
                      {l.listed && l.delistUrl && (
                        <a href={l.delistUrl} target="_blank" rel="noreferrer" className="ml-2 text-sky-700 underline">
                          Delist
                        </a>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          <Card
            title="Inbox placement tests"
            subtitle="Sends a tokenised probe to seed mailboxes, then reads them to record the folder."
            actions={
              <Button
                variant="primary"
                loading={runSeed.isPending}
                disabled={!seed.identityId}
                onClick={() => runSeed.mutate()}
              >
                Run test
              </Button>
            }
          >
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <Field label="Send from">
                <Select value={seed.identityId} onChange={(e) => setSeed({ ...seed, identityId: e.target.value })}>
                  <option value="">Select a mailbox</option>
                  {data.identities.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.email}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Test name">
                <Input value={seed.name} onChange={(e) => setSeed({ ...seed, name: e.target.value })} />
              </Field>
            </div>

            {data.seedTests.length === 0 ? (
              <EmptyState title="No tests run for this domain yet" />
            ) : (
              <Table head={['Test', 'Inbox', 'Junk', 'Missing', 'Inbox rate', 'Status']}>
                {data.seedTests.map((t) => (
                  <tr key={t.id}>
                    <Td className="font-medium text-ink-900">{t.name}</Td>
                    <Td className="tabular-nums">{t.inbox}</Td>
                    <Td className="tabular-nums">{t.junk}</Td>
                    <Td className="tabular-nums">{t.missing}</Td>
                    <Td>{t.total ? pct((t.inbox / t.total) * 100, 0) : '--'}</Td>
                    <Td>
                      <Badge tone={t.status === 'completed' ? 'neutral' : 'info'}>{t.status}</Badge>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>

        <Card
          title="Mailboxes"
          subtitle="Each from-address has its own caps, minimum gap and warmup curve. Campaigns rotate across them."
          actions={
            <div className="flex items-end gap-2">
              <Input
                placeholder="local part"
                className="w-32"
                value={mailbox.localPart}
                onChange={(e) => setMailbox({ ...mailbox, localPart: e.target.value })}
              />
              <Input
                placeholder="Display name"
                className="w-44"
                value={mailbox.displayName}
                onChange={(e) => setMailbox({ ...mailbox, displayName: e.target.value })}
              />
              <Button
                loading={addMailbox.isPending}
                disabled={!mailbox.localPart}
                onClick={() => addMailbox.mutate()}
              >
                Add
              </Button>
            </div>
          }
        >
          {data.identities.length === 0 ? (
            <EmptyState title="No mailboxes yet" body="A campaign needs at least one sending identity." />
          ) : (
            <Table head={['Address', 'Display name', 'Status', 'Daily cap', 'Hourly cap', 'Min gap', '']}>
              {data.identities.map((i) => {
                const sending = i.status === 'active' || i.status === 'warming';
                return (
                  <tr key={i.id}>
                    <Td className="font-mono text-xs font-medium text-ink-900">{i.email}</Td>
                    <Td>{i.displayName}</Td>
                    <Td>
                      <Badge tone={i.status === 'active' ? 'good' : i.status === 'warming' ? 'info' : 'warn'}>
                        {i.status}
                      </Badge>
                    </Td>
                    <Td className="tabular-nums">{i.dailyCap}</Td>
                    <Td className="tabular-nums">{i.hourlyCap}</Td>
                    <Td className="tabular-nums">{i.minGapSeconds}s</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1.5">
                        {sending ? (
                          <Button
                            loading={setIdentityStatus.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Pause ' +
                                    i.email +
                                    '?\n\nIt stops taking new sends, and any messages already queued against it are held until you resume it.'
                                )
                              ) {
                                setIdentityStatus.mutate({ id: i.id, status: 'paused' });
                              }
                            }}
                          >
                            Pause
                          </Button>
                        ) : (
                          <Button
                            loading={setIdentityStatus.isPending}
                            onClick={() => setIdentityStatus.mutate({ id: i.id, status: 'active' })}
                          >
                            Resume
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          loading={removeIdentity.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                'Remove ' +
                                  i.email +
                                  ' from this domain?\n\nRefused if it has already sent anything - send history has to stay attributable to the address it came from. Pause it instead in that case.'
                              )
                            ) {
                              removeIdentity.mutate(i.id);
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
